import Auth from '@/services/auth/auth';
import i18n from '@/i18n';

export function handleAllErrors (error: any): Error {
	const errorResponseData = error?.response?.data;
	// Lambda authorizer returned a 403 meaning the user token is either invalid or expired, log them out
	if (error?.response?.status === 403 && error?.code === 'ERR_BAD_REQUEST' && error?.name === 'AxiosError') {
		window.B2BApp ? window.B2BApp.backToApp() : Auth.logout();
		throw (i18n.t('shared.error_timed_out'));
	}
	else if (errorResponseData) {
		return handleAPIError(errorResponseData);
	}
	else {
		throw (error);
	}
}

/**
 * Throw the respective error depending on the status code.
 * There are specific error codes that we wanna return a
 * personalized message, others not.
 *
 * @param {APIError} error
 */
export function handleAPIError (error: APIError): Error {
	// Check if the error is type of string and is a network
	// failure, meaning there is something wrong with the session.
	// IE: token has expired.
	if (error.message === 'Network Error') {
		error = {
			statusCode: 401,
			error: 'Forbidden',
			message: i18n.t('shared.error_timed_out')
		};
	}

	if (error.statusCode === 401) {
		window.B2BApp ? window.B2BApp.backToApp() : Auth.logout();
		throw (i18n.t('shared.error_timed_out'));
	}
	if (error.statusCode === 403) {
		window.B2BApp ? window.B2BApp.backToApp() : Auth.logout();
		throw (i18n.t('shared.error_tenant_not_recognized'));
	}
	if (error.statusCode === 500) {
		if (error.code === 2305) {
			throw ('preauth_failed');
		}
		if (error.code === 2308) {
			throw (i18n.t('shared.error_order_cannot_be_completed_before_due_date'));
		}
		if (error.code === 2501) {
			throw (i18n.t('shared.error_request_deposit_amount_exceeds_limit', { requestedAmount: i18n.n(error.requestedAmount ? +error.requestedAmount?.toFixed(2) : 0, 'currency'), outstandingAmount: i18n.n(error.requestLimit ? +error.requestLimit?.toFixed(2) : 0, 'currency') }));
		}
		if (error.code === 1803) {
			const reasonMessages = (error.details?.reasons ?? [])
				.filter((reason) => i18n.te(`shared.error_pos_refusal_${reason}`))
				.map((reason) => i18n.t(`shared.error_pos_refusal_${reason}`));

			if (reasonMessages.length) {
				throw (reasonMessages.join(' '));
			}
			throw (typeof error.message === 'string' ? error.message : i18n.t('shared.error_internal_server_error'));
		}
		if (error.code === 2602) {
			throw (i18n.t('shared.error_no_invoice_numbers_left'));
		}
		throw (i18n.t('shared.error_internal_server_error'));
	}
	throw (typeof error.message === 'string' ? error.message : error.message[0]);
}

/**
 * Safely convert an unknown error to a display string.
 * handleAllErrors may throw a string, an Error, or the raw axios error — stringifying the
 * latter directly produces "[object Object]". This helper normalizes all three.
 *
 * @param {unknown} error
 * @return {string}
 */
export function toErrorMessage(error: unknown): string {
	if (typeof error === 'string') {
		return error;
	}
	if (error instanceof Error && error.message) {
		return error.message;
	}
	return i18n.t('shared.error_internal_server_error');
}

export function scrollToFirstError(): void {
	setTimeout(() => {
		const el = document.getElementsByClassName('error-input')[0] as HTMLElement;
		if (el) {
			el.scrollIntoView({ behavior: 'smooth', block: 'start', inline: 'nearest' });
		}
	}, 250);
}
